import { object, ref, string } from 'yup';
import { Form, Formik } from 'formik';
import React, { FC } from 'react';

// Models
import { ContactFormValue } from '../../../models/Forms/ContactFormValue';
import { FormikDropdownOption } from '../../../models/Formik';
import { ContactFormSubject } from '../../../models/Responses/ContactFormPageContent';

// ii18n
import { useTranslation } from '../../../translations/useTranslation';

// Components
import { Button } from '../../Button';
import {
  FormikDropdown,
  FormikFilePicker,
  FormikInput,
  FormikTextArea,
} from '../../formik';

interface Props {
  formValues: ContactFormValue;
  onSubmit: (values: ContactFormValue) => Promise<void>;
  subjects: ContactFormSubject[];
  isRedirecting: boolean;
}

const ContactForm: FC<Props> = ({
  formValues,
  onSubmit,
  subjects,
  isRedirecting,
}) => {
  const { t, m, keys } = useTranslation();
  const validationSchema = object().shape({
    name: string().required(t(keys.errorRequired)),
    subject: object().test(
      'titleHasValue',
      t(keys.errorOneOf),
      function (value) {
        return value?.Title !== undefined || value?.Title === '';
      }
    ),
    email: string()
      .required(t(keys.errorRequired))
      .email(t(keys.errorInvalidEmail)),
    emailRepeated: string()
      .required(t(keys.errorRequired))
      .oneOf(
        [ref('email')],
        m(
          keys.errorMismatch,
          '{otherField}',
          t(keys.formEmailAddress).toLocaleLowerCase()
        )
      ),
    message: string().required(t(keys.errorRequired)),
  });

  const subjectOptions: FormikDropdownOption[] = subjects.map((item) => ({
    label: item.Title,
    value: item,
  }));

  return (
    <>
      <h2 className="form-app__heading">{t(keys.contactFormTitle)}</h2>
      <Formik
        initialValues={formValues}
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, isValid, submitForm }) => (
          <Form>
            <fieldset className="aea-form-fieldset">
              <FormikDropdown
                name="subject"
                placeholder={t(keys.contactFormSubjectInputPlaceholder)}
                label={`${t(keys.contactFormSubjectInput)} *`}
                options={subjectOptions}
              />
              <FormikInput
                label={`${t(keys.formName)} *`}
                type="text"
                name="name"
              />
              <FormikInput
                label={`${t(keys.formEmailAddress)} *`}
                type="text"
                name="email"
              />
              <FormikInput
                label={`${m(
                  keys.formRepeat,
                  '{fieldName}',
                  t(keys.formEmailAddress).toLowerCase()
                )} *`}
                type="text"
                name="emailRepeated"
              />
              <FormikInput
                label={t(keys.formSocialSecurityNumber)}
                subLabel={t(keys.contactFormSocialSecurityNumberSubLabel)}
                type="text"
                name="socialSecurityNumber"
              />
              <FormikInput
                label={`${t(keys.formPhoneNumber)}`}
                type="text"
                name="phone"
              />
              <FormikTextArea
                rows={8}
                label={`${t(keys.formMessage)} *`}
                name="message"
              />
              <FormikFilePicker
                name="attachment"
                label={t(keys.formAttachFile)}
              />
            </fieldset>
            <Button
              title={t(keys.uiSubmit)}
              onClick={submitForm}
              disabled={!isValid}
              isLoading={isSubmitting || isRedirecting}
            />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ContactForm;
